import React, { useEffect, useMemo, useState } from 'react';
import { Platform, View, Text, Pressable, StyleSheet } from 'react-native';
import type {
  AccountPracInfoResponseDto,
  BillPayProductDto,
  AccountPracInfoType,
  BillPaySubscriptionDto,
  BillpayCheckoutRequestDto,
  BillpayCheckoutResponseDto,
  BillpayInitSessionRequestDto,
  BillpayInitSessionResponseDto,
  BillpayAddSubscriptionRequestDto,
  BillpayAddSubscriptionResponseDto,
  BillpayCancelSubscriptionRequestDto,
  BillpayCancelSubscriptionResponseDto,
} from '../../../types/api';
import { EDITOR_COLORS, editorText } from '../../../styles/editorTheme';
import { AccountPracInfoService } from '../../../services/account';
import { AccountPracInfoStorage } from '../../../storage/AccountPracInfoStorage';
import { resolveAuthContext } from '../../../services/auth/authContext';

interface SubscriptionViewProps {
  accountData?: AccountPracInfoResponseDto | null;
  isLoading?: boolean;
  error?: string | null;
  storage?: any;
  accountId?: string | null;
  apiService?: any;
}

type ProductGroupKind = 'add' | 'addon' | 'change' | 'upgrade';

interface ProductGroupBlock {
  kind: 'group';
  group: ProductGroupKind;
  title: string;
  headerPrefix: string;
  saveLabel: string;
  products: BillPayProductDto[];
}

const parseAccessType = (value: string | undefined): number | null => {
  const parsed = parseInt((value ?? '').trim(), 10);
  return Number.isNaN(parsed) ? null : parsed;
};

const CURRENT_KEY = '__current__';
const CANCEL_KEY = '__cancel__';

// '2' = Stripe, matching the existing account_verify_stripe_checkout call in App.tsx.
const BP_PROC_ID_STRIPE = '2';

// Not in EDITOR_COLORS (a normal-severity "gray/blue" theme) — Cancel is the one
// destructive action on this view, so it gets its own light-red treatment.
const LIGHT_RED_BG = '#fecaca';
const LIGHT_RED_TEXT = '#ef4444';

const resolveApiService = (apiService?: any): any => {
  return apiService
    || (globalThis as any).__proGroupsApiService
    || (globalThis as any).__childNavApiService;
};

const resolveUserStore = (): any => resolveAuthContext().userStore;

async function callBillpayService<TResponse>(
  apiService: any,
  storage: any,
  requestType: string,
  payload: object
): Promise<TResponse> {
  const response = await apiService.processServiceRequestWithRefresh({
    RequestType: requestType,
    Payload: JSON.stringify(payload),
  }, 'sync', {
    userStore: resolveUserStore(),
    storage,
  });

  if (!response?.success) {
    throw new Error(response?.message || `${requestType} failed.`);
  }

  if (typeof response.payload === 'string' && response.payload) {
    try {
      return JSON.parse(response.payload) as TResponse;
    } catch {
      return {} as TResponse;
    }
  }
  return (response.payload ?? {}) as TResponse;
}

export const SubscriptionView: React.FC<SubscriptionViewProps> = ({
  accountData,
  isLoading,
  error,
  storage,
  accountId,
  apiService,
}) => {
  const resolvedApiService = useMemo(() => resolveApiService(apiService), [apiService]);

  const billPayInfo = accountData?.BillPayInfo;
  const products = billPayInfo?.Products ?? [];
  const subscriptions = billPayInfo?.Subscriptions ?? [];
  const customerId = billPayInfo?.CustomerId || '';

  // The account's active subscription, if any — first entry in BillPayInfo.Subscriptions.
  // TODO: once Status values are finalized, filter to an explicitly-active one instead.
  const currentSubscription = subscriptions[0] ?? null;
  // A subscription's ProductId now lives per SubscriptionItem — normally just one (the
  // primary/base product), but an added optional product shows up as a second (or later)
  // item on the same subscription. SubscriptionItems[0] is treated as the primary; anything
  // after it is an already-added optional product. SubscriptionItems can carry more than one
  // item for the same ProductId (e.g. a per-seat/usage line item split across billing
  // periods) — dedupe by ProductId (Set preserves first-occurrence order) so the same product
  // isn't listed/cancelable more than once.
  const subscribedProductIds = Array.from(
    new Set(currentSubscription?.SubscriptionItems?.map((item) => item.ProductId) ?? [])
  );
  const subscribedProducts = subscribedProductIds
    .map((productId) => products.find((product) => product.ProductId === productId))
    .filter((product): product is BillPayProductDto => !!product);
  const currentProduct = subscribedProducts[0] ?? null;
  const additionalSubscribedProducts = subscribedProducts.slice(1);

  const practiceAccessType = accountData?.AccountSettings?.PracInfo?.AccessType ?? '';
  // Baseline used for group membership: the current subscription's own tier when one exists,
  // otherwise the practice's configured AccessType (PracInfoDto.AccessType).
  const currentAccessType = currentProduct?.AccessType || practiceAccessType;

  const groupBlocks = useMemo<ProductGroupBlock[]>(() => {
    const result: ProductGroupBlock[] = [];
    // Every product already on the subscription — the primary plus any already-added
    // optional products — must be excluded from Change/Upgrade/Add-on-eligibility below;
    // they're not alternatives to switch to or new add-ons to offer, they're already active.
    const subscribedProductIdSet = new Set(subscribedProductIds);
    // A product with any (real, non-empty-string) OptionalInfo entries is inherently an
    // add-on-type product — never a standalone plan, only ever a supplement to whichever base
    // product(s) it lists. It must only ever appear via Add-on options (and only when the
    // current subscription's product is one of the ones it lists) — never as an Add/Change/
    // Upgrade option, even when its own OptionalInfo doesn't reference the current product at
    // all (i.e. it's not currently eligible as an add-on here).
    const isOptionalTypeProduct = (product: BillPayProductDto) => product.OptionalInfo.length > 0;

    if (!currentProduct) {
      const addProducts = products.filter(
        (product) => product.AccessType === practiceAccessType && !isOptionalTypeProduct(product)
      );
      if (addProducts.length > 0) {
        result.push({
          kind: 'group',
          group: 'add',
          title: 'Select subscription to add',
          headerPrefix: '',
          saveLabel: 'Add Now',
          products: addProducts,
        });
      }
    } else {
      // Add-ons: a product lists (in OptionalInfo) the base ProductIds it's a valid add-on
      // for — if the current subscription's product is one of them, it's offered here,
      // attached to the existing subscription rather than replacing it. This is independent
      // of the add-on product's own AccessType (an add-on's tier has no bearing on which base
      // products it attaches to — it can differ freely from the current plan's AccessType).
      // Already-subscribed products (primary or already-added optional) are excluded.
      const addOnProducts = products.filter(
        (product) => product.OptionalInfo.includes(currentProduct.ProductId) && !subscribedProductIdSet.has(product.ProductId)
      );

      const changeProducts = products.filter(
        (product) => product.AccessType === currentAccessType
          && !subscribedProductIdSet.has(product.ProductId)
          && !isOptionalTypeProduct(product)
      );
      if (changeProducts.length > 0) {
        result.push({
          kind: 'group',
          group: 'change',
          title: 'Change plan options',
          headerPrefix: 'Change to ',
          saveLabel: 'Change Now',
          products: changeProducts,
        });
      }

      if (addOnProducts.length > 0) {
        result.push({
          kind: 'group',
          group: 'addon',
          title: 'Add-on options',
          headerPrefix: '',
          saveLabel: 'Add Now',
          products: addOnProducts,
        });
      }
    }

    const currentAccessTypeNum = parseAccessType(currentAccessType);
    const upgradeProducts = currentAccessTypeNum === null
      ? []
      : products.filter((product) => {
        const productAccessType = parseAccessType(product.AccessType);
        return productAccessType !== null
          && productAccessType > currentAccessTypeNum
          && !subscribedProductIdSet.has(product.ProductId)
          && !isOptionalTypeProduct(product);
      });
    if (upgradeProducts.length > 0) {
      result.push({
        kind: 'group',
        group: 'upgrade',
        title: 'Upgrade options',
        headerPrefix: 'Upgrade to ',
        saveLabel: 'Upgrade Now',
        products: upgradeProducts,
      });
    }

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, currentProduct, subscribedProductIds, practiceAccessType, currentAccessType]);

  const validKeys = useMemo(() => {
    const keys = [CURRENT_KEY];
    if (currentProduct) {
      keys.push(CANCEL_KEY);
    }
    groupBlocks.forEach((block) => block.products.forEach((product) => keys.push(product.ProductId)));
    return keys;
  }, [groupBlocks, currentProduct]);

  const [selectedKey, setSelectedKey] = useState<string>(CURRENT_KEY);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveIsError, setSaveIsError] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Keep the selection valid as accountData refreshes/arrives — e.g. Cancel/Manage Billing
  // stop applying if the current subscription disappears, or a product drops out of its group.
  useEffect(() => {
    if (!validKeys.includes(selectedKey)) {
      setSelectedKey(CURRENT_KEY);
      setSaveMessage(null);
      setSaveIsError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validKeys.join('|')]);

  const handleSelect = (key: string) => {
    if (key === selectedKey || isProcessing) {
      return;
    }
    setSelectedKey(key);
    setSaveMessage(null);
    setSaveIsError(false);
  };

  const selectedGroupBlock = groupBlocks.find((block) => block.products.some((p) => p.ProductId === selectedKey));
  const selectedGroupProduct = selectedGroupBlock?.products.find((p) => p.ProductId === selectedKey) ?? null;

  // Re-fetches just BillPayInfo (the 'Subscriptions' background type), merges it into the
  // locally-cached AccountPracInfoResponseDto (other fields are left untouched — a Types:
  // ['Subscriptions'] request doesn't return them), and notifies (see notifyConfigUpdated)
  // to pick up the refreshed cache.
  const refreshBillPayInfo = async () => {
    if (!accountId || !resolvedApiService) {
      return;
    }
    try {
      const pracInfoService = new AccountPracInfoService(resolvedApiService);
      const fresh = await pracInfoService.getAccountPracInfo(
        { AccountId: accountId, Types: ['Subscriptions'] },
        { userStore: resolveUserStore(), storage }
      );

      if (storage) {
        const pracStorage = new AccountPracInfoStorage(storage);
        const cached = await pracStorage.getAccountPracInfo(accountId);
        const merged = cached ? { ...cached, BillPayInfo: fresh.BillPayInfo } : fresh;
        await pracStorage.saveAccountPracInfo(accountId, merged);
      }

      notifyConfigUpdated();
    } catch (refreshError) {
      console.warn('SubscriptionView: failed to refresh BillPayInfo after action', refreshError);
    }
  };

  // Unlike a same-tier Change/Add/Add-on/Cancel — where only BillPayInfo itself changes and
  // applySubscriptionUpdate's narrow local patch is all that's needed — an Upgrade also has
  // the backend update the practice's AccessType (AccountSettings.PracInfo.AccessType) and
  // the signed-in SRA's own AccessType (SraUser.AccessType) to match the new subscription's
  // tier. Neither of those comes back from billpay_add_subscription's response, so after an
  // upgrade this does a full AccountSettings + Subscriptions re-fetch instead, to pick up
  // both updated AccessType values alongside the new subscription.
  const refreshAfterUpgrade = async () => {
    if (!accountId || !resolvedApiService) {
      return;
    }
    try {
      const pracInfoService = new AccountPracInfoService(resolvedApiService);
      const types: AccountPracInfoType[] = ['AccountSettings', 'Subscriptions'];
      const fresh = await pracInfoService.getAccountPracInfo(
        { AccountId: accountId, Types: types },
        { userStore: resolveUserStore(), storage }
      );

      if (storage) {
        const pracStorage = new AccountPracInfoStorage(storage);
        const cached = await pracStorage.getAccountPracInfo(accountId);
        const merged = cached
          ? { ...cached, AccountSettings: fresh.AccountSettings, SraUser: fresh.SraUser, BillPayInfo: fresh.BillPayInfo }
          : fresh;
        await pracStorage.saveAccountPracInfo(accountId, merged);
      }

      notifyConfigUpdated();
    } catch (refreshError) {
      console.warn('SubscriptionView: failed to refresh account info after upgrade', refreshError);
    }
  };

  // Deliberately NOT progroups:sra-config-updated — that event is also watched by
  // ProGroupsShell, which bumps sraVisibilityRefreshToken on every occurrence, and that
  // token is baked into ProGroupsAccountView's own `key` prop, so dispatching it force-
  // remounts this whole view. That destroyed in-flight actions (Change's cancel-then-add
  // got torn down mid-flight) and, even for a single Add, raced the remount's own fresh
  // read against this component's already-correct write. progroups:billpay-info-updated is
  // a dedicated event ProGroupsAccountView also listens for (see its own useEffect) that
  // only re-reads the cache into state — no remount. Callers doing a multi-step operation
  // should pass notify: false on every step but the last (or the failure path, so the UI
  // still reflects reality if the later step never completes).
  const notifyConfigUpdated = () => {
    if (accountId && typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('progroups:billpay-info-updated', { detail: { accountId } }));
    }
  };

  // billpay_add_subscription / billpay_cancel_subscription now return the affected
  // BillPaySubscriptionDto directly (an empty/default one — no SubscriptionId — means fully
  // removed), so the local cache can be patched precisely instead of doing a full refetch.
  // targetSubscriptionId identifies which existing entry to replace/remove; it's ignored when
  // the returned subscription is non-empty and doesn't match any existing entry (a new add).
  const applySubscriptionUpdate = async (
    targetSubscriptionId: string,
    subscription: BillPaySubscriptionDto | null | undefined,
    options: { notify?: boolean } = {}
  ) => {
    const { notify = true } = options;
    if (!accountId || !storage) {
      return;
    }
    try {
      const pracStorage = new AccountPracInfoStorage(storage);
      const cached = await pracStorage.getAccountPracInfo(accountId);
      if (!cached?.BillPayInfo) {
        await refreshBillPayInfo();
        return;
      }

      const existingSubscriptions = cached.BillPayInfo.Subscriptions ?? [];
      const isRemoved = !subscription || !subscription.SubscriptionId;
      const nextSubscriptions = isRemoved
        ? existingSubscriptions.filter((s) => s.SubscriptionId !== targetSubscriptionId)
        : (() => {
          const idx = existingSubscriptions.findIndex((s) => s.SubscriptionId === subscription!.SubscriptionId);
          return idx >= 0
            ? existingSubscriptions.map((s, i) => (i === idx ? subscription! : s))
            : [...existingSubscriptions, subscription!];
        })();

      const merged = {
        ...cached,
        BillPayInfo: { ...cached.BillPayInfo, Subscriptions: nextSubscriptions },
      };
      await pracStorage.saveAccountPracInfo(accountId, merged);

      if (notify) {
        notifyConfigUpdated();
      }
    } catch (updateError) {
      console.warn('SubscriptionView: failed to apply subscription update locally', updateError);
    }
  };

  // Picks up a completed billpay_init_create Stripe Checkout on return — App.tsx stamps
  // this global when it detects ?billpay=success&session_id=... on the app's URL. Verifying
  // tells the backend to sync the new customer/subscription from Stripe; refreshBillPayInfo
  // then pulls that synced data back into this view. Cancel returns need no verify call —
  // App.tsx already routed back here via __proGroupsPendingAccountSubsection alone.
  useEffect(() => {
    const sessionId = (globalThis as any).__billpayCheckoutSessionId;
    if (!sessionId || !resolvedApiService?.processServiceRequestWithRefresh) {
      return;
    }
    delete (globalThis as any).__billpayCheckoutSessionId;

    (async () => {
      setIsProcessing(true);
      setSaveMessage(null);
      setSaveIsError(false);
      try {
        await callBillpayService(resolvedApiService, storage, 'account_verify_stripe_checkout', {
          BPProcId: BP_PROC_ID_STRIPE,
          CheckoutId: sessionId,
        });
        await refreshBillPayInfo();
        setSaveMessage('Subscription confirmed.');
      } catch (err: any) {
        setSaveIsError(true);
        setSaveMessage(err?.message || 'Could not confirm the completed checkout.');
      } finally {
        setIsProcessing(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedApiService]);

  // No billpay account yet — send the practice through Stripe Checkout to create one. Shared
  // by handleAddSubscription and handleChangeSubscription's upgrade-with-no-customerId path
  // (an upgrade with no existing subscription and no billpay account is really just a
  // first-time add).
  const startCheckoutForProduct = async (product: BillPayProductDto) => {
    if (typeof window === 'undefined') {
      throw new Error('Checkout is only available in the web app.');
    }
    const origin = window.location.origin;
    const request: BillpayCheckoutRequestDto = {
      AccountId: accountId || '',
      Email: accountData?.SraUser?.Email || '',
      BPProcId: BP_PROC_ID_STRIPE,
      BPProdList: product.ProductId,
      // App.tsx reads these `billpay=` params on return — distinct from the older
      // `upgrade=` premium-tier flow — and lands back on this same Subscription tab.
      SuccessURL: `${origin}/?billpay=success&session_id={CHECKOUT_SESSION_ID}`,
      CancelURL: `${origin}/?billpay=cancel`,
    };
    const response = await callBillpayService<BillpayCheckoutResponseDto>(
      resolvedApiService, storage, 'billpay_init_create', request
    );
    if (!response.CheckoutURL) {
      throw new Error('No checkout URL returned.');
    }
    window.location.href = response.CheckoutURL;
  };

  const handleAddSubscription = async (product: BillPayProductDto) => {
    if (!resolvedApiService?.processServiceRequestWithRefresh) {
      setSaveIsError(true);
      setSaveMessage('Could not proceed: billing service unavailable.');
      return;
    }
    setIsProcessing(true);
    setSaveMessage(null);
    setSaveIsError(false);
    try {
      if (!customerId) {
        await startCheckoutForProduct(product);
        return;
      }

      const request: BillpayAddSubscriptionRequestDto = {
        BPProcId: BP_PROC_ID_STRIPE,
        CustomerId: customerId,
        BPProdId: product.ProductId,
        SubscriptionId: '', // blank = normal processing: create a new subscription
      };
      const response = await callBillpayService<BillpayAddSubscriptionResponseDto>(
        resolvedApiService, storage, 'billpay_add_subscription', request
      );
      await applySubscriptionUpdate('', response.Subscription);
      setSaveMessage('Subscription added.');
    } catch (err: any) {
      setSaveIsError(true);
      setSaveMessage(err?.message || 'Could not add the subscription.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddOnSubscription = async (product: BillPayProductDto) => {
    if (!resolvedApiService?.processServiceRequestWithRefresh || !customerId || !currentSubscription) {
      setSaveIsError(true);
      setSaveMessage('Could not proceed: no active subscription to add on to.');
      return;
    }
    setIsProcessing(true);
    setSaveMessage(null);
    setSaveIsError(false);
    try {
      const request: BillpayAddSubscriptionRequestDto = {
        BPProcId: BP_PROC_ID_STRIPE,
        CustomerId: customerId,
        BPProdId: product.ProductId,
        SubscriptionId: currentSubscription.SubscriptionId, // attach to the existing subscription
      };
      const response = await callBillpayService<BillpayAddSubscriptionResponseDto>(
        resolvedApiService, storage, 'billpay_add_subscription', request
      );
      await applySubscriptionUpdate(currentSubscription.SubscriptionId, response.Subscription);
      setSaveMessage('Add-on added.');
    } catch (err: any) {
      setSaveIsError(true);
      setSaveMessage(err?.message || 'Could not add the add-on.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleChangeSubscription = async (
    product: BillPayProductDto,
    actionLabel: string,
    options: { isUpgrade?: boolean } = {}
  ) => {
    const { isUpgrade = false } = options;
    if (!resolvedApiService?.processServiceRequestWithRefresh) {
      setSaveIsError(true);
      setSaveMessage('Could not proceed: billing service unavailable.');
      return;
    }
    // Change always requires an existing subscription to change (groupBlocks only ever offers
    // 'change' when one exists). Upgrade is always allowed, with or without one — with none,
    // there's nothing to cancel first, and it's really just a first-time add (see the
    // no-customerId branch below).
    if (!isUpgrade && (!customerId || !currentSubscription)) {
      setSaveIsError(true);
      setSaveMessage('Could not proceed: no active subscription to change.');
      return;
    }

    setIsProcessing(true);
    setSaveMessage(null);
    setSaveIsError(false);

    if (currentSubscription) {
      const targetSubscriptionId = currentSubscription.SubscriptionId;
      try {
        const cancelRequest: BillpayCancelSubscriptionRequestDto = {
          BPProcId: BP_PROC_ID_STRIPE,
          CustomerId: customerId,
          SubscriptionId: targetSubscriptionId,
          BPProdId: '', // blank = normal processing: cancel the entire subscription
        };
        const cancelResponse = await callBillpayService<BillpayCancelSubscriptionResponseDto>(
          resolvedApiService, storage, 'billpay_cancel_subscription', cancelRequest
        );
        // Don't notify yet — this is the middle of a two-step operation. Dispatching here
        // would remount this view (see notifyConfigUpdated's comment) before the add step
        // below ever runs, and the user would see the cancel "win" while the add is still
        // in flight.
        await applySubscriptionUpdate(targetSubscriptionId, cancelResponse.Subscription, { notify: false });
      } catch (err: any) {
        setSaveIsError(true);
        setSaveMessage(err?.message || 'Could not cancel the current subscription — the change was not made.');
        setIsProcessing(false);
        return;
      }
    }

    try {
      if (!customerId) {
        await startCheckoutForProduct(product);
        return;
      }

      const addRequest: BillpayAddSubscriptionRequestDto = {
        BPProcId: BP_PROC_ID_STRIPE,
        CustomerId: customerId,
        BPProdId: product.ProductId,
        SubscriptionId: '', // blank = normal processing: create a new subscription
      };
      const addResponse = await callBillpayService<BillpayAddSubscriptionResponseDto>(
        resolvedApiService, storage, 'billpay_add_subscription', addRequest
      );
      if (isUpgrade) {
        // Local patch first for immediate feedback (notify: false — refreshAfterUpgrade
        // below does the authoritative full pull, including PracInfo.AccessType/
        // SraUser.AccessType, which this add response doesn't carry).
        await applySubscriptionUpdate('', addResponse.Subscription, { notify: false });
        await refreshAfterUpgrade();
      } else {
        await applySubscriptionUpdate('', addResponse.Subscription);
      }
      setSaveMessage(`${actionLabel} complete.`);
    } catch (err: any) {
      setSaveIsError(true);
      if (currentSubscription) {
        // The cancel above already went through — this is now the true end state, so notify
        // so the rest of the app reflects the practice actually being cancelled rather than
        // sitting on the pre-cancel view forever.
        notifyConfigUpdated();
        setSaveMessage(
          `Previous subscription was cancelled, but ${err?.message ? err.message.toLowerCase() : 'the new plan could not be added'}. Please contact support.`
        );
      } else {
        setSaveMessage(err?.message || `Could not complete the ${actionLabel.toLowerCase()}.`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  // productIdToCancel omitted/blank = normal processing: cancel the entire subscription
  // (primary product). Specified = remove just that one product/item, leaving the rest of
  // the subscription (including any other added optional products) active.
  const handleCancelSubscription = async (productIdToCancel?: string) => {
    if (!resolvedApiService?.processServiceRequestWithRefresh || !customerId || !currentSubscription) {
      setSaveIsError(true);
      setSaveMessage('Could not proceed: no active subscription to cancel.');
      return;
    }
    const targetSubscriptionId = currentSubscription.SubscriptionId;

    setIsProcessing(true);
    setSaveMessage(null);
    setSaveIsError(false);
    try {
      const request: BillpayCancelSubscriptionRequestDto = {
        BPProcId: BP_PROC_ID_STRIPE,
        CustomerId: customerId,
        SubscriptionId: targetSubscriptionId,
        BPProdId: productIdToCancel || '',
      };
      const response = await callBillpayService<BillpayCancelSubscriptionResponseDto>(
        resolvedApiService, storage, 'billpay_cancel_subscription', request
      );
      await applySubscriptionUpdate(targetSubscriptionId, response.Subscription);
      setSaveMessage(productIdToCancel ? 'Add-on cancelled.' : 'Subscription cancelled.');
    } catch (err: any) {
      setSaveIsError(true);
      setSaveMessage(err?.message || 'Could not cancel the subscription.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleManageBilling = async () => {
    if (!resolvedApiService?.processServiceRequestWithRefresh || !customerId) {
      setSaveIsError(true);
      setSaveMessage('Could not proceed: no billing account found.');
      return;
    }
    setIsProcessing(true);
    setSaveMessage(null);
    setSaveIsError(false);
    try {
      if (typeof window === 'undefined') {
        throw new Error('Manage Billing is only available in the web app.');
      }
      const request: BillpayInitSessionRequestDto = {
        BPProcId: BP_PROC_ID_STRIPE,
        CustomerId: customerId,
        ReturnUrl: `${window.location.origin}${window.location.pathname}`,
      };
      const response = await callBillpayService<BillpayInitSessionResponseDto>(
        resolvedApiService, storage, 'billpay_init_session', request
      );
      if (!response.SessionUrl) {
        throw new Error('No billing session URL returned.');
      }
      window.location.href = response.SessionUrl;
    } catch (err: any) {
      setSaveIsError(true);
      setSaveMessage(err?.message || 'Could not open the billing portal.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (error) {
    return (
      <View style={styles.panel}>
        <View style={styles.errorCard}>
          <Text style={styles.errorTitle}>Error</Text>
          <Text style={styles.errorBody}>{error}</Text>
        </View>
      </View>
    );
  }

  if (isLoading) {
    return (
      <View style={styles.panel}>
        <Text style={styles.loadingText}>Loading subscription information...</Text>
      </View>
    );
  }

  const renderDescription = (html: string) => (
    Platform.OS === 'web' ? (
      React.createElement('div', {
        style: { ...editorText, color: EDITOR_COLORS.textSecondary, lineHeight: '18px' },
        dangerouslySetInnerHTML: { __html: html },
      })
    ) : (
      <Text style={styles.descriptionFallback}>{html}</Text>
    )
  );

  const renderActionButton = (label: string, onPress: () => void, variant: 'default' | 'danger' = 'default') => (
    <View style={styles.saveButtonRow}>
      <Pressable
        style={[styles.saveButton, variant === 'danger' && styles.saveButtonDanger, isProcessing && styles.saveButtonDisabled]}
        onPress={onPress}
        disabled={isProcessing}
      >
        <Text style={styles.saveButtonText}>{isProcessing ? 'Processing...' : label}</Text>
      </Pressable>
    </View>
  );

  return (
    <View style={styles.panel}>
      <View style={styles.body}>
        <View style={styles.menuColumn}>
          <MenuLink
            label="Current subscription"
            isSelected={selectedKey === CURRENT_KEY}
            onPress={() => handleSelect(CURRENT_KEY)}
          />

          {customerId ? (
            <MenuLink
              label="Manage Billing"
              isSelected={false}
              disabled={isProcessing}
              onPress={handleManageBilling}
            />
          ) : null}

          {groupBlocks.map((block) => (
            <React.Fragment key={block.group}>
              <View style={styles.menuSpacerRow} />
              <Text style={styles.groupHeader}>{block.title}</Text>
              {block.products.map((product) => (
                <View key={product.ProductId} style={styles.groupMemberRow}>
                  <MenuLink
                    label={product.Name}
                    isSelected={selectedKey === product.ProductId}
                    onPress={() => handleSelect(product.ProductId)}
                  />
                </View>
              ))}
            </React.Fragment>
          ))}

          {currentProduct ? (
            <>
              <View style={styles.menuSpacerRowLarge} />
              <MenuLink
                label="Cancel subscription"
                isSelected={selectedKey === CANCEL_KEY}
                onPress={() => handleSelect(CANCEL_KEY)}
              />
            </>
          ) : null}
        </View>

        <View style={styles.contentColumn}>
          {saveMessage ? (
            <View style={styles.saveMessageRow}>
              <Text style={saveIsError ? styles.saveMessageError : styles.saveMessageText}>{saveMessage}</Text>
            </View>
          ) : null}

          {selectedKey === CURRENT_KEY ? (
            currentProduct ? (
              <>
                <Text style={styles.contentHeader}>{currentProduct.DName}</Text>
                {renderDescription(currentProduct.Description)}
                {additionalSubscribedProducts.map((product) => (
                  <View key={product.ProductId} style={styles.additionalProductBlock}>
                    <Text style={styles.contentHeader}>{product.DName}</Text>
                    {renderDescription(product.Description)}
                  </View>
                ))}
              </>
            ) : (
              <Text style={styles.noSubscriptionText}>No Subscription Found</Text>
            )
          ) : selectedKey === CANCEL_KEY && currentProduct ? (
            <>
              <Text style={styles.contentHeader}>{`Cancel ${currentProduct.DName}`}</Text>
              {renderDescription('Services will be suspended, but you can still access your reports and data.')}
              {renderActionButton('Cancel Now', () => handleCancelSubscription(), 'danger')}
              {additionalSubscribedProducts.map((product) => (
                <React.Fragment key={product.ProductId}>
                  <View style={styles.cancelItemSpacer} />
                  <Text style={styles.contentHeader}>{`Cancel ${product.DName}`}</Text>
                  {renderDescription('This add-on will be removed; the rest of your subscription will stay active.')}
                  {renderActionButton('Cancel Now', () => handleCancelSubscription(product.ProductId), 'danger')}
                </React.Fragment>
              ))}
            </>
          ) : selectedGroupProduct && selectedGroupBlock ? (
            <>
              <Text style={styles.contentHeader}>{`${selectedGroupBlock.headerPrefix}${selectedGroupProduct.DName}`}</Text>
              {renderDescription(selectedGroupProduct.Description)}
              {renderActionButton(
                selectedGroupBlock.saveLabel,
                selectedGroupBlock.group === 'add'
                  ? () => handleAddSubscription(selectedGroupProduct)
                  : selectedGroupBlock.group === 'addon'
                  ? () => handleAddOnSubscription(selectedGroupProduct)
                  : selectedGroupBlock.group === 'upgrade'
                  ? () => handleChangeSubscription(selectedGroupProduct, selectedGroupBlock.saveLabel, { isUpgrade: true })
                  : () => handleChangeSubscription(selectedGroupProduct, selectedGroupBlock.saveLabel)
              )}
            </>
          ) : null}
        </View>
      </View>
    </View>
  );
};

const MenuLink: React.FC<{ label: string; isSelected: boolean; onPress: () => void; disabled?: boolean }> = ({ label, isSelected, onPress, disabled }) => (
  <Pressable style={styles.menuLink} onPress={onPress} disabled={disabled}>
    <Text style={[styles.menuLinkText, isSelected && styles.menuLinkTextSelected, disabled && styles.menuLinkTextDisabled]}>{label}</Text>
  </Pressable>
);

const styles = StyleSheet.create({
  panel: {
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 14,
    paddingBottom: 20,
    backgroundColor: EDITOR_COLORS.background,
  },
  loadingText: {
    ...editorText,
    color: EDITOR_COLORS.textMuted,
  },
  errorCard: {
    backgroundColor: EDITOR_COLORS.errorBackground,
    borderRadius: 12,
    padding: 16,
  },
  errorTitle: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
    marginBottom: 4,
  },
  errorBody: {
    ...editorText,
    lineHeight: 20,
    color: EDITOR_COLORS.errorText,
  },
  body: {
    flexDirection: 'row',
  },
  menuColumn: {
    width: 200,
    borderRightWidth: 1,
    borderRightColor: EDITOR_COLORS.separator,
    paddingRight: 12,
    paddingTop: 4,
  },
  menuSpacerRow: {
    height: 16,
  },
  menuSpacerRowLarge: {
    height: 40,
  },
  menuLink: {
    paddingVertical: 4,
  },
  menuLinkText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  menuLinkTextSelected: {
    fontWeight: '700',
  },
  menuLinkTextDisabled: {
    opacity: 0.5,
  },
  groupHeader: {
    ...editorText,
    fontStyle: 'italic',
    color: EDITOR_COLORS.textSecondary,
    marginBottom: 4,
  },
  groupMemberRow: {
    paddingLeft: 14,
  },
  contentColumn: {
    flex: 1,
    paddingLeft: 24,
    paddingTop: 4,
  },
  contentHeader: {
    ...editorText,
    fontWeight: '700',
    textAlign: 'center',
    color: EDITOR_COLORS.textPrimary,
  },
  additionalProductBlock: {
    marginTop: 20,
  },
  cancelItemSpacer: {
    height: 32,
  },
  descriptionFallback: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    lineHeight: 18,
  },
  noSubscriptionText: {
    ...editorText,
    color: LIGHT_RED_TEXT,
    textAlign: 'center',
    marginTop: 32,
  },
  placeholderValue: {
    ...editorText,
    color: EDITOR_COLORS.placeholder,
  },
  saveMessageRow: {
    marginBottom: 16,
  },
  saveMessageText: {
    ...editorText,
    color: EDITOR_COLORS.successText,
  },
  saveMessageError: {
    ...editorText,
    color: EDITOR_COLORS.errorText,
  },
  saveButtonRow: {
    alignItems: 'center',
    marginTop: 16,
  },
  saveButton: {
    minWidth: 160,
    alignItems: 'center',
    paddingLeft: 20,
    paddingRight: 20,
    paddingTop: 10,
    paddingBottom: 10,
    backgroundColor: EDITOR_COLORS.submitBackground,
    borderRadius: 6,
  },
  saveButtonDanger: {
    backgroundColor: LIGHT_RED_BG,
  },
  saveButtonDisabled: {
    opacity: 0.6,
  },
  saveButtonText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
});
